import * as amplitude from "@amplitude/analytics-browser";
import { datadogLogs } from "@datadog/browser-logs";
import { useHandler } from "@redotech/react-util/hook";
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { AuthContext } from "../app/auth";
import { Card } from "../order/returnFlow/cardContents/instant-refund";
import { SettingsContext } from "./settings";

export interface User {
  email: string;
  name: string;
  guestEmail?: string;
  guestName?: string;
  address?: string;
  instantRefund?: {
    astraUserId: string;
    card: Card;
  };
}

const NAME_TOKEN_KEY = "redo.user_name_token";
const EMAIL_TOKEN_KEY = "redo.email_token";
const GUEST_NAME_TOKEN_KEY = "redo.guest_name_token";
const GUEST_EMAIL_TOKEN_KEY = "redo.guest_email_token";
const ADDRESS_TOKEN_KEY = "redo.address_token";

export const UserContext = createContext<User | undefined>(undefined);

export const SetUserContext =
  createContext<(user: User | undefined) => void>(undefined);

export const UserContextProvider = ({ children }) => {
  const [user, setUserState] = useState<User | undefined>(undefined);
  const setUser = (value: User | undefined) => {
    if (user) {
      amplitude.reset();
    }
    if (value) {
      amplitude.init(process.env.AMPLITUDE_API_KEY!, {
        defaultTracking: true,
        userId: value.guestEmail || value.email,
      });
      datadogLogs.setUser({ email: value.guestEmail || value.email });
    }
    setUserState(value);
  };
  const auth = useContext(AuthContext);
  const settings = useContext(SettingsContext);
  const storeURL: string = settings?.storeUrl;

  useEffect(() => {
    if (user && storeURL) {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("storeURL", storeURL);
      amplitude.identify(identifyEvent);
      datadogLogs.setGlobalContextProperty("storeURL", storeURL);
    }
  }, [user, storeURL]);

  useLayoutEffect(() => {
    if (auth) {
      setUser({
        name: localStorage.getItem(NAME_TOKEN_KEY),
        email: localStorage.getItem(EMAIL_TOKEN_KEY),
        guestEmail: localStorage.getItem(GUEST_EMAIL_TOKEN_KEY),
        guestName: localStorage.getItem(GUEST_NAME_TOKEN_KEY),
        address: localStorage.getItem(ADDRESS_TOKEN_KEY),
      });
    } else {
      setUser(undefined);
    }
  }, [auth]);

  const setUser_ = useHandler((user: User | undefined) => {
    setUser(user);
    if (user) {
      localStorage.setItem(NAME_TOKEN_KEY, user.name);
      localStorage.setItem(EMAIL_TOKEN_KEY, user.email);
      user.guestEmail
        ? localStorage.setItem(GUEST_EMAIL_TOKEN_KEY, user.guestEmail)
        : localStorage.removeItem(GUEST_EMAIL_TOKEN_KEY);
      user.guestName
        ? localStorage.setItem(GUEST_NAME_TOKEN_KEY, user.guestName)
        : localStorage.removeItem(GUEST_NAME_TOKEN_KEY);
      user.address
        ? localStorage.setItem(ADDRESS_TOKEN_KEY, user.address)
        : localStorage.removeItem(ADDRESS_TOKEN_KEY);
    } else {
      localStorage.removeItem(NAME_TOKEN_KEY);
      localStorage.removeItem(EMAIL_TOKEN_KEY);
      localStorage.removeItem(GUEST_EMAIL_TOKEN_KEY);
      localStorage.removeItem(GUEST_NAME_TOKEN_KEY);
      localStorage.removeItem(ADDRESS_TOKEN_KEY);
    }
  });

  return (
    <UserContext.Provider value={user}>
      <SetUserContext.Provider value={setUser_}>
        {children}
      </SetUserContext.Provider>
    </UserContext.Provider>
  );
};
